// src/features/platform/PlatformCreateDialog.tsx
// Create a platform — form-kit dialog (docs/ux/14-forms.md), opened from
// PlatformListPage's "+ New" instead of routing to a /platforms/new page.
// Follows the TenantCreateDialog / TeamCreateDialog precedent exactly.

import { useEffect, useState } from 'react';
import { useI18n } from '@react/hooks';
import { usePlatform } from '@react/hooks';
import { FormDialog, TitleInput, AutoGrowTextarea, FormRow } from '@/components/form';

/** Slugify a name into a URL-safe slug preview. */
function slugify(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[\s_]+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

export interface PlatformCreateDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (id: string | undefined) => void;
}

export function PlatformCreateDialog({ open, onClose, onCreated }: PlatformCreateDialogProps) {
  const { t } = useI18n();
  const store = usePlatform();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [moreFields, setMoreFields] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName('');
    setDescription('');
    setSlug('');
    setSlugTouched(false);
    setMoreFields(false);
    setError(null);
  }, [open]);

  const effectiveSlug = slugTouched ? slug : slugify(name);
  const dirty = !!(name.trim() || description.trim());

  const submit = async () => {
    if (!name.trim() || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await store.CreatePlatform({
        name: name.trim(),
        slug: effectiveSlug || slugify(name.trim()),
        description: description.trim(),
      } as never);
      const newId = (result as any)?.entity?.id || (result as any)?.id;
      onCreated(newId);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      onSubmit={submit}
      submitLabel={t('platform.create')}
      canSubmit={!!name.trim()}
      isSubmitting={submitting}
      dirty={dirty}
      aria-label={t('platform.create')}
      footerLeft={
        <button
          type="button"
          onClick={() => setMoreFields((v) => !v)}
          className="text-xs text-muted-foreground hover:text-foreground"
        >
          {moreFields ? t('common.fewerFields') : t('common.moreFields')}
        </button>
      }
    >
      <div className="space-y-1 px-3.5 pb-2 pt-2">
        {error && <p className="rounded-md bg-destructive/10 px-2 py-1 text-xs text-destructive">{error}</p>}
        <TitleInput
          value={name}
          onChange={setName}
          placeholder={t('platform.namePlaceholder')}
          initialFocus
          aria-label={t('platform.name')}
        />
        <AutoGrowTextarea
          value={description}
          onChange={setDescription}
          placeholder={t('platform.descriptionPlaceholder')}
          minRows={1}
        />
      </div>

      {moreFields && (
        <div className="space-y-3 border-t border-border px-3.5 py-3">
          <FormRow label={t('platform.slug')} labelWidth="sm" hint={t('platform.slugHint')}>
            <input
              type="text"
              value={effectiveSlug}
              onChange={(e) => {
                setSlugTouched(true);
                setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''));
              }}
              placeholder="my-platform"
              className="w-full max-w-xs rounded-md border border-input bg-background px-3 py-1.5 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </FormRow>
        </div>
      )}
    </FormDialog>
  );
}

export default PlatformCreateDialog;
